import { useState } from 'react';
import { ScrollView, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Calendar } from 'app/design/primitives/calendar';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { festivalCalendarDate, festivalDateString, isValidFestivalDate } from 'app/lib/festival-date';
import { useFestivalToday } from 'app/hooks/use-festival-today';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

/** Mount a fresh editor for each opening so cancel never changes the saved value. */
export function FestivalDateEditor({
  value,
  onClose,
  onSave,
  pending = false,
  error,
}: {
  value?: string | null;
  onClose: () => void;
  onSave: (date: string) => void;
  pending?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation('festival');
  const theme = useTheme();
  const today = useFestivalToday();
  const [selected, setSelected] = useState(value && isValidFestivalDate(value, today) ? value : undefined);
  const close = () => {
    if (!pending) onClose();
  };
  return (
    <Modal open transparent onClose={close} animationType="fade">
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.md"
        padding="spacing.general.x6"
        gap="spacing.general.x8"
        width="92%"
        maxWidth={600}
        maxHeight="90%"
        testID="festival-date-editor"
      >
        <Row alignItems="center" gap="spacing.general.x4">
          <Text variant="bodyLargeMedium" flex={1}>
            {t('editTitle')}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('close')}
            disabled={pending}
            onPress={close}
            hitSlop={12}
          >
            <IconX size={32} color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} />
          </Pressable>
        </Row>
        <ScrollView>
          <Calendar
            initial={festivalCalendarDate(selected ?? today)}
            selected={selected ? festivalCalendarDate(selected) : undefined}
            minDate={festivalCalendarDate(today)}
            onDatePress={(date) => setSelected(festivalDateString(date))}
            disabled={pending}
          />
        </ScrollView>
        {error ? (
          <Text accessibilityRole="alert" color="text.error">
            {t('saveError')}
          </Text>
        ) : null}
        <Row justifyContent="flex-end" gap="spacing.general.x4" flexWrap="wrap">
          <Button variant="secondary" flexDirection="column" maxWidth="100%" disabled={pending} onPress={close}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            flexDirection="column"
            maxWidth="100%"
            testID="festival-date-save"
            loading={pending}
            disabled={!isValidFestivalDate(selected, today)}
            onPress={() => selected && onSave(selected)}
          >
            {t('save')}
          </Button>
        </Row>
      </Column>
    </Modal>
  );
}
